import { FC } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';

export interface FAQItemProps {
  /**
   * The question to display
   */
  question: string;
  /**
   * The answer to display when expanded
   */
  answer: string;
  /**
   * Whether the item is currently expanded
   */
  isOpen: boolean;
  /**
   * Callback when the item is toggled
   */
  onToggle: () => void;
  /**
   * Optional additional className
   */
  className?: string;
}

/**
 * Individual FAQ accordion item component.
 * Displays a question header that can be clicked to reveal/hide the answer.
 *
 * @example
 * <FAQItem
 *   question="How long is a session?"
 *   answer="Sessions range from 30 minutes to 2 hours."
 *   isOpen={isOpen}
 *   onToggle={() => setIsOpen(!isOpen)}
 * />
 */
export const FAQItem: FC<FAQItemProps> = ({ question, answer, isOpen, onToggle, className }) => {
  return (
    <div
      className={cn('border-b border-gray-200 dark:border-slate-700 last:border-b-0', className)}
    >
      {/* Question header - clickable */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full items-center justify-between py-5 text-left',
          'transition-colors duration-200',
          'hover:text-primary-600 dark:hover:text-primary-400',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800',
          isOpen && 'text-primary-600 dark:text-primary-400'
        )}
        aria-expanded={isOpen}
      >
        <span className="pr-4 text-base font-medium text-gray-900 dark:text-slate-100 md:text-lg">
          {question}
        </span>
        <ChevronDown
          className={cn(
            'h-5 w-5 flex-shrink-0 text-gray-500 dark:text-slate-400',
            'transition-transform duration-200',
            isOpen && 'rotate-180 text-primary-600 dark:text-primary-400'
          )}
          aria-hidden="true"
        />
      </button>

      {/* Answer content - collapsible */}
      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
        aria-hidden={!isOpen}
      >
        <div className="overflow-hidden">
          <p className="pb-5 text-gray-600 dark:text-slate-400 leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
};

FAQItem.displayName = 'FAQItem';
